// Request priority helper for React Query and axios
// This helps manage concurrent requests more efficiently
import { useQuery } from "react-query";
import { queuedQueryFn, Priority } from "./requestScheduler";

// Authentication check helper
const isUserAuthenticated = () => {
  try {
    // Check if we're in a browser environment
    if (typeof window === "undefined") return false;

    // Try to get auth state from Redux store if available
    if (typeof window.__REDUX_STORE__ !== "undefined") {
      const state = window.__REDUX_STORE__.getState();
      return !!(state?.auth?.authToken && state?.auth?.user);
    }

    // Fallback: check localStorage and sessionStorage for auth token
    let token = null;

    // Check localStorage (safer)
    if (typeof window.localStorage !== "undefined") {
      token = localStorage.getItem("authToken") || localStorage.getItem("token");
    }

    // Check sessionStorage if no token in localStorage
    if (!token && typeof window.sessionStorage !== "undefined") {
      token = sessionStorage.getItem("authToken");
    }

    return !!token;
  } catch (error) {
    console.warn("Auth check failed:", error);
    return false;
  }
};

export const REQUEST_PRIORITIES = {
  HIGH: "high", // User actions, mutations, critical UI data
  NORMAL: "normal", // Regular data fetching
  LOW: "low", // Background data, prefetching
};

// Convert our priority to scheduler priority
const toSchedulerPriority = (priority) => {
  switch (priority) {
    case REQUEST_PRIORITIES.HIGH:
    case REQUEST_PRIORITIES.NORMAL:
      return Priority.HIGH;
    case REQUEST_PRIORITIES.LOW:
      return Priority.LOW;
    default:
      return Priority.HIGH;
  }
};

// React Query configurations based on request priority and current system load
export const getQueryConfig = (priority = REQUEST_PRIORITIES.NORMAL, options = {}) => {
  const { requireAuth = true, respectLoadLimits = true } = options;

  // Chrome-specific configurations
  const isChrome = typeof window !== "undefined" && !!window.chrome;

  const baseConfig = {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  };

  switch (priority) {
    case REQUEST_PRIORITIES.HIGH:
      return {
        ...baseConfig,
        staleTime: 1 * 60 * 1000, // 1 minute - always fresh for user actions
        cacheTime: 5 * 60 * 1000, // 5 minutes in cache
        retry: 2, // Retry high-priority requests more
        retryDelay: 500, // Faster retry
      };

    case REQUEST_PRIORITIES.NORMAL:
      return {
        ...baseConfig,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes in cache
        retry: 1,
        retryDelay: 1000,
      };

    case REQUEST_PRIORITIES.LOW:
      return {
        ...baseConfig,
        staleTime: 15 * 60 * 1000, // 15 minutes - background data can be stale longer
        cacheTime: isChrome ? 10 * 60 * 1000 : 30 * 60 * 1000, // Shorter cache time for Chrome to reduce persistence issues
        retry: 0, // Don't retry background requests to avoid database overload
        retryOnMount: false, // Don't retry when component remounts
        networkMode: "offlineFirst", // Use cached data when network is slow
        // Chrome-specific: More aggressive cache invalidation
        refetchOnWindowFocus: isChrome ? false : false,
        refetchInterval: isChrome ? false : false, // Disable auto refetch in Chrome to prevent unauthorized queries
        // Note: enabled will be handled by individual hooks for proper load management
      };

    default:
      return baseConfig;
  }
};

// Wrapper function to create scheduled query functions with error handling
export const createScheduledQueryFn = (queryFn, priority = REQUEST_PRIORITIES.NORMAL, name) => {
  const schedulerPriority = toSchedulerPriority(priority);

  // Wrap with enhanced error handling for large datasets
  const wrappedQueryFn = async (...args) => {
    try {
      return await queryFn(...args);
    } catch (error) {
      // Enhanced error handling for common large dataset issues
      if (error.code === "NS_BINDING_ABORTED" || error.name === "AbortError") {
        console.warn(
          `Request aborted for ${name}. This might indicate a timeout with large datasets.`
        );
        throw new Error(`Request timeout - dataset too large. Consider implementing pagination.`);
      }

      if (error.code === "NETWORK_ERROR" || error.message?.includes("timeout")) {
        console.warn(`Network/timeout error for ${name}:`, error.message);
        throw new Error(
          `Network timeout - this usually happens with large datasets (>1000 records)`
        );
      }

      // Re-throw original error for other cases
      throw error;
    }
  };

  return queuedQueryFn(wrappedQueryFn, schedulerPriority, name);
};

// Helper to determine request priority based on query key or context
export const inferRequestPriority = (queryKey, context = {}) => {
  if (typeof queryKey === "string") {
    // High priority patterns
    if (queryKey.includes("user") || queryKey.includes("auth") || queryKey.includes("profile")) {
      return REQUEST_PRIORITIES.HIGH;
    }

    // Low priority patterns
    if (
      queryKey.includes("background") ||
      queryKey.includes("prefetch") ||
      queryKey.includes("analytics")
    ) {
      return REQUEST_PRIORITIES.LOW;
    }
  }

  if (Array.isArray(queryKey)) {
    const keyString = queryKey.join("_").toLowerCase();

    // High priority patterns
    if (keyString.includes("user") || keyString.includes("auth") || keyString.includes("current")) {
      return REQUEST_PRIORITIES.HIGH;
    }

    // Low priority patterns
    if (
      keyString.includes("list") ||
      keyString.includes("all") ||
      keyString.includes("background")
    ) {
      return REQUEST_PRIORITIES.LOW;
    }
  }

  // Check context for user interactions
  if (context.isUserAction || context.isImmediate) {
    return REQUEST_PRIORITIES.HIGH;
  }

  if (context.isBackground || context.isPrefetch) {
    return REQUEST_PRIORITIES.LOW;
  }

  return REQUEST_PRIORITIES.NORMAL;
};

// Batch request helper - groups related requests to execute together
export class RequestBatcher {
  constructor() {
    this.batches = new Map();
    this.timers = new Map();
  }

  // Add request to batch (will execute after batchDelay ms)
  addToBatch(batchKey, requestFn, batchDelay = 50) {
    if (!this.batches.has(batchKey)) {
      this.batches.set(batchKey, []);
    }

    this.batches.get(batchKey).push(requestFn);

    // Clear existing timer and set new one
    if (this.timers.has(batchKey)) {
      clearTimeout(this.timers.get(batchKey));
    }

    const timer = setTimeout(() => {
      this.executeBatch(batchKey);
    }, batchDelay);

    this.timers.set(batchKey, timer);
  }

  // Execute all requests in a batch
  async executeBatch(batchKey) {
    const requests = this.batches.get(batchKey);
    if (!requests || requests.length === 0) return;

    // Execute all requests in parallel
    try {
      const results = await Promise.allSettled(requests.map((fn) => fn()));

      return results;
    } finally {
      // Clean up
      this.batches.delete(batchKey);
      this.timers.delete(batchKey);
    }
  }
}

export const requestBatcher = new RequestBatcher();

// Query load manager to prevent database overload
export class QueryLoadManager {
  constructor(maxConcurrentQueries = 1) {
    this.maxConcurrent = maxConcurrentQueries;
    this.activeQueries = new Set();
  }

  // Register a query as active
  registerQuery(queryKey) {
    console.log("ADDING TO QUERY", this.activeQueries);
    this.activeQueries.add(queryKey);
  }

  // Unregister a query when it completes
  unregisterQuery(queryKey) {
    this.activeQueries.delete(queryKey);
  }

  // Check if we can execute a new query based on current load
  canExecuteQuery(priority = REQUEST_PRIORITIES.NORMAL) {
    const currentLoad = this.activeQueries.size;

    // Always allow high priority queries
    if (priority === REQUEST_PRIORITIES.HIGH) {
      return true;
    }

    // For normal and low priority, check concurrent limit
    console.log("CHECK_QUERY", currentLoad, this.maxConcurrent);
    return currentLoad < this.maxConcurrent;
  }

  // Get current database load
  getCurrentLoad() {
    return {
      active: this.activeQueries.size,
      max: this.maxConcurrent,
      isOverloaded: this.activeQueries.size >= this.maxConcurrent,
    };
  }
}

export const queryLoadManager = new QueryLoadManager(1); // Limit to 1 concurrent low-priority database query

// Helper function to easily convert existing useQuery calls
export const useScheduledQuery = (queryKey, queryFn, options = {}) => {
  const { priority = REQUEST_PRIORITIES.NORMAL, name, ...queryOptions } = options;
  const scheduledQueryFn = createScheduledQueryFn(queryFn, priority, name);

  return useQuery(queryKey, scheduledQueryFn, {
    ...getQueryConfig(priority),
    ...queryOptions,
  });
};

// Usage examples:
//
// // BEFORE (old way):
// const { data } = useQuery(['tree', treeId], () => getTree(treeId), { ... });
//
// // AFTER (new scheduled way):
// const { data } = useScheduledQuery(['tree', treeId], () => getTree(treeId), {
//   priority: REQUEST_PRIORITIES.HIGH, // or NORMAL, LOW
//   name: 'getTree',
//   // ...other useQuery options
// });
//
// // Or using createScheduledQueryFn directly:
// const scheduledGetTree = createScheduledQueryFn(() => getTree(treeId), REQUEST_PRIORITIES.HIGH, 'getTree');
// const { data } = useQuery(['tree', treeId], scheduledGetTree, getQueryConfig(REQUEST_PRIORITIES.HIGH));
//
// // For background data (LOW priority):
// const { data } = useScheduledQuery(['analytics', 'pageviews'], () => getAnalytics(), {
//   priority: REQUEST_PRIORITIES.LOW,
//   name: 'getAnalytics'
// });
//
// // Batch related requests:
// requestBatcher.addToBatch('projectDetails', () => getProject(id));
// requestBatcher.addToBatch('projectDetails', () => getProjectTrees(id));
// requestBatcher.addToBatch('projectDetails', () => getProjectSensors(id));
